import type { Supplier } from '@amarantos/api-client'
import { getListSuppliersQueryOptions, useListSuppliersSuspense } from '@amarantos/api-client'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useDeferredValue } from 'react'
import { useTranslation } from 'react-i18next'
import { pick } from 'remeda'
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog'
import { DeleteButton } from '@/components/delete-button'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SupplierFormDrawer, type SupplierFormDrawerState } from '@/features/suppliers/components/supplier-form-drawer'
import { SuppliersTable } from '@/features/suppliers/components/suppliers-table'
import { useDeleteSupplierMutation } from '@/features/suppliers/hooks/use-supplier-mutations'
import { type SuppliersSearch, suppliersSearchSchema, toListSuppliersParams } from '@/features/suppliers/utils/search'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { useDebouncedInput } from '@/hooks/use-debounced-input'
import { getApiErrorMessage } from '@/utils/errors'

export const Route = createFileRoute('/_authenticated/suppliers/')({
	validateSearch: suppliersSearchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(getListSuppliersQueryOptions(toListSuppliersParams(deps)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_suppliers' />
	),
	component: SuppliersRoute,
})

function SuppliersRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()
	const deferredSearch = useDeferredValue(search)
	const isStale = deferredSearch !== search

	const suppliersQuery = useListSuppliersSuspense(toListSuppliersParams(deferredSearch))

	const deleteSupplier = useConfirmAction<Supplier, { id: number }>({
		mutation: useDeleteSupplierMutation(),
		toVariables: (supplier) => ({ id: supplier.id }),
		formatError: (err) => getApiErrorMessage(err, t('error.delete_supplier')),
	})

	const suppliers = suppliersQuery.data?.data ?? []
	const meta = suppliersQuery.data?.meta
	const currentPage = search.page
	const totalPages = meta?.total_pages ?? 1
	const drawerState: SupplierFormDrawerState = pick(search, ['mode', 'supplierId'])
	const drawerOpen = drawerState.mode !== undefined

	const onSearchChange = useCallback(
		(nextValue: string) => {
			navigate({
				replace: true,
				search: (previous) => ({
					...previous,
					search: nextValue,
					page: 1,
				}),
			})
		},
		[navigate],
	)

	const searchInputProps = useDebouncedInput({
		value: search.search,
		onDebouncedChange: onSearchChange,
	})

	const onSortChange = useCallback(
		(nextValue: SuppliersSearch['sort']) => {
			navigate({
				replace: true,
				search: (previous) => ({
					...previous,
					sort: nextValue,
					page: 1,
				}),
			})
		},
		[navigate],
	)

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.suppliers') }]}>
				<PageTitle
					subtitle={t('suppliers.page.description')}
					actions={
						<Button asChild>
							<Link to='.' search={(previous) => ({ ...previous, mode: 'create' as const, supplierId: undefined })}>
								{t('suppliers.create.button')}
							</Link>
						</Button>
					}
				>
					{t('nav.suppliers')}
				</PageTitle>
			</PageHeader>
			<PageContent>
				<Card>
					<CardContent className='space-y-4'>
						<div className='flex items-center gap-2'>
							<Input {...searchInputProps} placeholder={t('suppliers.search.placeholder')} className='max-w-sm' />

							<Select value={search.sort} onValueChange={onSortChange}>
								<SelectTrigger aria-label={t('suppliers.sort.label')} className='w-45'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='name'>{t('suppliers.sort.name')}</SelectItem>
									<SelectItem value='created_at'>{t('suppliers.sort.created_at')}</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<SuppliersTable
							suppliers={suppliers}
							isStale={isStale}
							actions={(supplier) => <DeleteButton onClick={() => deleteSupplier.request(supplier)} />}
						/>

						<PaginationBar
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
						/>
					</CardContent>
				</Card>

				<SupplierFormDrawer
					{...drawerState}
					open={drawerOpen}
					onOpenChange={(isOpen) => {
						if (!isOpen) {
							navigate({
								search: (previous) => ({
									...previous,
									mode: undefined,
									supplierId: undefined,
								}),
							})
						}
					}}
				/>

				<ConfirmDeleteDialog
					{...deleteSupplier.dialogProps}
					title={t('suppliers.delete.title')}
					description={t('suppliers.delete.description', {
						name: deleteSupplier.target?.company_name ?? t('suppliers.delete.fallback_name'),
					})}
				/>
			</PageContent>
		</>
	)
}
